import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check, ChevronDown, Globe2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { languageLabels, type Language, useI18n } from '../i18n';
import './LanguageSelector.css';

const languages: Language[] = ['ru', 'en', 'az'];

export function LanguageSelector() {
  const [open, setOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const { language, setLanguage, t } = useI18n();

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return (
    <>
      <motion.button
        type="button"
        aria-label={t('language')}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="language-selector-trigger"
        onClick={() => setOpen(true)}
        whileTap={reduceMotion ? undefined : { scale: 0.94 }}
      >
        <span className="language-selector-trigger__icon" aria-hidden="true"><Globe2 size={16} strokeWidth={1.8} /></span>
        <span className="language-selector-trigger__code">{language.toUpperCase()}</span>
        <ChevronDown className="language-selector-trigger__chevron" size={13} strokeWidth={2} aria-hidden="true" />
      </motion.button>

      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {open && (
          <motion.div
            className="language-selector-overlay"
            style={{ zIndex: 10001 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.2 }}
            onClick={() => setOpen(false)}
          >
            <motion.section
              role="dialog"
              aria-modal="true"
              aria-label={t('chooseLanguage')}
              className="language-selector-sheet"
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 46, scale: 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 30, scale: 0.99 }}
              transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 430, damping: 34, mass: 0.75 }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="language-selector-sheet__handle" aria-hidden="true" />

              <header className="language-selector-sheet__header">
                <div className="language-selector-sheet__heading">
                  <span className="language-selector-sheet__heading-icon" aria-hidden="true"><Globe2 size={18} strokeWidth={1.7} /></span>
                  <div>
                    <h2>{t('chooseLanguage')}</h2>
                    <p>{t('languageDescription')}</p>
                  </div>
                </div>
                <button type="button" className="language-selector-sheet__close" onClick={() => setOpen(false)} aria-label={t('close')}>
                  <X size={18} strokeWidth={2} />
                </button>
              </header>

              <div className="language-selector-options" role="listbox" aria-label={t('language')}>
                {languages.map((option) => {
                  const selected = language === option;
                  return (
                    <motion.button
                      type="button"
                      key={option}
                      role="option"
                      aria-selected={selected}
                      className={`language-selector-option${selected ? ' language-selector-option--selected' : ''}`}
                      whileTap={reduceMotion ? undefined : { scale: 0.985 }}
                      onClick={() => {
                        setLanguage(option);
                        setOpen(false);
                      }}
                    >
                      <span className="language-selector-option__label">{languageLabels[option]}</span>
                      <span className="language-selector-option__right">
                        <span className="language-selector-option__code">{option.toUpperCase()}</span>
                        <span className="language-selector-option__check" aria-hidden="true">{selected && <Check size={16} strokeWidth={2.5} />}</span>
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.section>
          </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}
